import React, { useState, useEffect } from 'react';
import { getAnalytics } from '../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Analytics({ shortCode }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [shortCode]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getAnalytics(shortCode);
      setAnalytics(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!analytics) {
    return null;
  }

  // Prepare chart data
  const countryData = {
    labels: Object.keys(analytics.clicksByCountry),
    datasets: [
      {
        label: 'Clicks by Country',
        data: Object.values(analytics.clicksByCountry),
        backgroundColor: 'rgba(52, 152, 219, 0.6)',
      },
    ],
  };

  const browserData = {
    labels: Object.keys(analytics.clicksByBrowser),
    datasets: [
      {
        label: 'Clicks',
        data: Object.values(analytics.clicksByBrowser),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
        ],
      },
    ],
  };

  const dateData = {
    labels: Object.keys(analytics.clicksByDate).sort(),
    datasets: [
      {
        label: 'Daily Clicks',
        data: Object.keys(analytics.clicksByDate)
          .sort()
          .map((date) => analytics.clicksByDate[date]),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
    ],
  };

  return (
    <div>
      <div className="card">
        <h2>Analytics for {shortCode}</h2>
        <div className="url-info">
          <p><strong>Original URL:</strong> {analytics.url.original}</p>
          {analytics.url.title && <p><strong>Title:</strong> {analytics.url.title}</p>}
          <p><strong>Created:</strong> {new Date(analytics.url.createdAt).toLocaleString()}</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>{analytics.summary.totalClicks}</h3>
          <p>Total Clicks</p>
        </div>
        <div className="stat-card">
          <h3>{analytics.summary.totalClicksLast24h}</h3>
          <p>Clicks (24h)</p>
        </div>
      </div>

      <div className="card chart-container">
        <h3>Clicks Over Time</h3>
        <Line data={dateData} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="card chart-container">
          <h3>Clicks by Country</h3>
          <Bar data={countryData} />
        </div>

        <div className="card chart-container">
          <h3>Clicks by Browser</h3>
          <Pie data={browserData} />
        </div>
      </div>

      <div className="card">
        <h3>Recent Clicks</h3>
        <ul className="url-list">
          {analytics.recentClicks.map((click, index) => (
            <li key={index} className="url-item">
              <div className="url-item-info">
                <p><strong>{click.city || 'Unknown'}, {click.country || 'Unknown'}</strong></p>
                <p style={{ fontSize: '0.9rem' }}>
                  {click.browser} on {click.os} ({click.device})
                </p>
                <p style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                  {new Date(click.clickedAt).toLocaleString()}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default Analytics;
